import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
    BarChart3, Users, Calendar, CreditCard, TrendingUp, 
    Activity, Scissors, Bell, RefreshCw, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminAnalyticsSection() {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [popularActivities, setPopularActivities] = useState([]);
    const [serviceBreakdown, setServiceBreakdown] = useState([]);
    const [memberEngagement, setMemberEngagement] = useState(null);
    const [creditSummary, setCreditSummary] = useState([]);

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const [overviewRes, popularRes, serviceRes, engagementRes, creditRes] = await Promise.all([
                axios.get(`${API}/admin/analytics/overview`, { headers }),
                axios.get(`${API}/admin/analytics/popular-activities?limit=5`, { headers }),
                axios.get(`${API}/admin/analytics/service-breakdown`, { headers }),
                axios.get(`${API}/admin/analytics/member-engagement`, { headers }),
                axios.get(`${API}/admin/analytics/credit-transactions?days=30`, { headers })
            ]);
            
            setOverview(overviewRes.data);
            setPopularActivities(popularRes.data);
            setServiceBreakdown(serviceRes.data);
            setMemberEngagement(engagementRes.data);
            setCreditSummary(creditRes.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'emerald' }) => (
        <div className="bg-white rounded-xl p-5 border border-stone-200 shadow-sm" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-stone-500 font-medium">{title}</p>
                    <p className="text-3xl font-bold text-stone-800 mt-1">{value}</p>
                    {subtitle && <p className="text-sm text-stone-500 mt-1">{subtitle}</p>}
                </div>
                <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 text-${color}-600`} />
                </div>
            </div>
            {trend !== undefined && (
                <div className={`flex items-center mt-3 text-sm ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {trend >= 0 ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
                    <span>{Math.abs(trend)}% from last month</span>
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-analytics">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-stone-800">Analytics Dashboard</h2>
                    <p className="text-stone-500 text-sm mt-1">Overview of your community's engagement</p>
                </div>
                <Button variant="outline" onClick={fetchAnalytics} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Overview Stats */}
            {overview && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard 
                        title="Active Members" 
                        value={overview.members.active}
                        subtitle={`${overview.members.total} total members`}
                        icon={Users}
                        color="blue"
                    />
                    <StatCard 
                        title="Bookings This Month" 
                        value={overview.bookings.this_month}
                        subtitle={`${overview.bookings.this_week} this week`}
                        icon={Calendar}
                        color="emerald"
                    />
                    <StatCard 
                        title="Service Appointments" 
                        value={overview.appointments.this_month}
                        subtitle={`${overview.appointments.total} total`}
                        icon={Scissors}
                        color="rose"
                    />
                    <StatCard 
                        title="Credits Purchased" 
                        value={overview.credits.this_month}
                        subtitle="This month"
                        icon={CreditCard}
                        color="purple"
                    />
                </div>
            )}

            {/* Second Row - Activities & Events */}
            {overview && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatCard 
                        title="Upcoming Activities" 
                        value={overview.activities.upcoming}
                        subtitle={`${overview.activities.total} active`}
                        icon={Activity}
                        color="amber"
                    />
                    <StatCard 
                        title="Upcoming Events" 
                        value={overview.events.upcoming}
                        subtitle={`${overview.events.total} active`}
                        icon={Calendar}
                        color="teal"
                    />
                    <StatCard 
                        title="Pending Notices" 
                        value={overview.community.pending_notices}
                        subtitle={`${overview.community.clubs} active clubs`}
                        icon={Bell}
                        color="orange"
                    />
                </div>
            )}

            {/* Member Engagement */}
            {memberEngagement && (
                <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Member Engagement (Last 30 Days)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div className="text-center">
                            <p className="text-3xl font-bold text-emerald-600">{memberEngagement.engagement_rate}%</p>
                            <p className="text-sm text-stone-500 mt-1">Engagement Rate</p>
                        </div>
                        <div className="text-center">
                            <p className="text-3xl font-bold text-blue-600">{memberEngagement.active_bookers_30d}</p>
                            <p className="text-sm text-stone-500 mt-1">Active Bookers</p>
                        </div>
                        <div className="text-center">
                            <p className="text-3xl font-bold text-rose-600">{memberEngagement.active_service_users_30d}</p>
                            <p className="text-sm text-stone-500 mt-1">Service Users</p>
                        </div>
                        <div className="text-center">
                            <p className="text-3xl font-bold text-purple-600">{memberEngagement.members_in_clubs}</p>
                            <p className="text-sm text-stone-500 mt-1">Club Members</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Popular Activities */}
                <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-emerald-600" />
                        Most Popular Activities
                    </h3>
                    {popularActivities.length === 0 ? (
                        <p className="text-stone-500 text-center py-8">No activity bookings yet</p>
                    ) : (
                        <div className="space-y-3">
                            {popularActivities.map((activity, index) => (
                                <div key={activity.activity_id} className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                                        index === 0 ? 'bg-amber-100 text-amber-700' :
                                        index === 1 ? 'bg-stone-200 text-stone-600' :
                                        index === 2 ? 'bg-orange-100 text-orange-700' :
                                        'bg-stone-100 text-stone-500'
                                    }`}>
                                        {index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-stone-800">{activity.title}</p>
                                        <p className="text-sm text-stone-500">{activity.category?.replace('_', ' ')}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-stone-800">{activity.booking_count}</p>
                                        <p className="text-xs text-stone-500">bookings</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Service Breakdown */}
                <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                        <Scissors className="w-5 h-5 text-rose-600" />
                        Service Appointments Breakdown
                    </h3>
                    {serviceBreakdown.length === 0 ? (
                        <p className="text-stone-500 text-center py-8">No service appointments yet</p>
                    ) : (
                        <div className="space-y-3">
                            {serviceBreakdown.map((service) => {
                                const total = serviceBreakdown.reduce((sum, s) => sum + s.count, 0);
                                const percentage = total > 0 ? Math.round((service.count / total) * 100) : 0;
                                return (
                                    <div key={service.service_type} className="space-y-1">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-medium text-stone-700">{service.label}</span>
                                            <span className="text-sm text-stone-500">{service.count} ({percentage}%)</span>
                                        </div>
                                        <div className="w-full bg-stone-100 rounded-full h-2">
                                            <div 
                                                className="bg-rose-500 h-2 rounded-full transition-all" 
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Credit Transactions Summary */}
            {creditSummary.length > 0 && (
                <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-purple-600" />
                        Credit Transactions (Last 30 Days)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {creditSummary.map((item) => (
                            <div key={item.type} className="text-center p-3 bg-stone-50 rounded-lg">
                                <p className={`text-2xl font-bold ${item.total_amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                    {item.total_amount >= 0 ? '+' : ''}{item.total_amount}
                                </p>
                                <p className="text-xs text-stone-500 mt-1">{item.label}</p>
                                <p className="text-xs text-stone-400">{item.count} transactions</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminAnalyticsSection;
